import React, { useEffect, useState } from 'react';
import { useForm } from 'react-hook-form';
import MediaUpload from '../../components/Forms/MediaUpload';
import { ActionButton } from '../../components/Forms/ActionButton';
import { toast, ToastContainer } from 'react-toastify';
import 'react-toastify/dist/ReactToastify.css';
import { useCreateEvent, useGetEventCategories, useGetEventDetailsById, useUpdateEvent } from '../../queries/event';
import { useLocation, useNavigate } from 'react-router-dom';
import Select from 'react-select';
import ActiveStatus from '../../components/Common/ActiveStatus';
import { useGetActiveOrganiser } from '../../queries/user';
import Dropdown from '../../components/Forms/Dropdown';

const CreateEvent = () => {

  const navigate = useNavigate();

  const { state } = useLocation();
  const eventId = state?.eventId;

  const { data: eventData, isPending: isEventDataPending } = useGetEventDetailsById(eventId);
  const { data: eventCategoryList, isPending: isEventCategoryListPending } = useGetEventCategories();
  const { data: organiserList, isPaused: isOrganiserListPending } = useGetActiveOrganiser();

  const [active, setActive] = useState(true);
  const [homePage, setHomePage] = useState(false);
  const [coverImage, setCoverImage] = useState(null);  // File object
  const [coverImageUrl, setCoverImageUrl] = useState(null);  // URL for display
  const [coverImageId, setCoverImageId] = useState(null);  // Image ID for updates
  const [clearMedia, setClearMedia] = useState(false);

  const { mutateAsync: createEvent } = useCreateEvent();
  const { mutateAsync: updateEvent } = useUpdateEvent(eventId);

  const {
    register,
    handleSubmit,
    formState: { errors },
    setValue,
    getValues,
    reset,
  } = useForm({
    defaultValues: {
      title: eventData ? eventData.title : "",
      description: eventData ? eventData.description : "",
      startDate: eventData ? eventData.startDate : "",
      endDate: eventData ? eventData.endDate : "",
      timingDetails: eventData ? eventData.timingDetails : "",
      instructionsForVolunteers: eventData ? eventData.instructionsForVolunteers : "",
      city: eventData ? eventData.city : "",
      location: eventData ? eventData.location : "",
      venueMapUrl: eventData ? eventData.venueMapUrl : "",
      eventOrganiser: eventData ? eventData.eventOrganiser : "",
      certificateArrangement: eventData ? eventData.certificateArrangement : "",
      typeOfEvent: eventData ? eventData.typeOfEvent?._id : "",
      points: eventData ? eventData.points?._id : "",
      eventSponsor: eventData ? eventData.eventSponsor?._id : "",
    }
  });

  const onSubmit = async (formData) => {
    let organiser = {};
    organiserList.filter(organizer => {
      if (organizer._id === formData.eventOrganiser) {
        const orgType = organizer.organiserType;
        if (orgType === 'NGO') {
          organiser.ngoId = organizer._id;
        } else if (orgType === 'Company') {
          organiser.companyId = organizer._id;
        }
      }
    });

    const submitData = {
      ...formData,
      organiser: organiser,
      active: active,
      isHomePage: homePage,
      coverImageId,
    };

    console.log({
      ...submitData,
      coverImage: coverImage ? 'File object' : coverImageUrl,
    });

    if (eventId) {
      toast.loading('Updating Event...');
      updateEvent({
        data: submitData,
        coverImage: coverImage,  // File object or null
      }, {
        onSuccess: (res) => {
          if(res.error) {
            toast.dismiss();
            toast.error('Error updating event! Please try again.');
            return;
          }
          toast.dismiss();
          toast.success('Event Updated Successfully!');
          reset();
          setCoverImage(null);
          setTimeout(() => {
            navigate('/events/events-main/view-events');
          }, 2000);
        },
        onError: (err) => {
          console.log(err);
          toast.dismiss();
          toast.error('Something Went Wrong!');
        }
      });
    } else {
      toast.loading('Creating Event...');
      createEvent({
        data: submitData,
        coverImage: coverImage,  // File object or null
      }, {
        onSuccess: () => {
          toast.dismiss();
          toast.success('Event Created Successfully!');
          reset();
          setCoverImage(null);
          setTimeout(() => {
            navigate('/events/events-main/view-events');
          }, 2000);
        },
        onError: (err) => {
          toast.dismiss();
          toast.error('Something Went Wrong!');
        }
      });
    }
  }

  useEffect(() => {
    if (!isEventDataPending && !isEventCategoryListPending && eventData) {
      setActive(eventData?.active);
      setHomePage(eventData?.isHomePage);
      setValue("title", eventData?.title);
      setValue("description", eventData?.description);
      setValue("startDate", eventData?.startDate.split("T")[0]);
      setValue("endDate", eventData?.endDate.split("T")[0]);
      // setValue("startTime", eventData?.startTime);
      setValue("timingDetails", eventData?.timingDetails);
      setValue("instructionsForVolunteers", eventData?.instructionsForVolunteers);
      // Handle both old (ObjectId with name) and new (string) city formats
      setValue("city", typeof eventData?.city === 'string' ? eventData?.city : eventData?.city?.name || '');
      setValue("location", eventData?.location);
      setValue("venueMapUrl", eventData?.venueMapUrl);
      setValue("eventOrganiser", eventData?.eventOrganiser.companyId || eventData?.eventOrganiser.ngoId);
      setValue("certificateArrangement", eventData?.certificateArrangement || 'NotSure');
      setValue("typeOfEvent", eventData?.typeOfEvent?._id);
      setValue("points", eventData?.points);
      setValue("eventSponsor", eventData?.eventSponsor);
      // Set URL and ID for Image API V2
      setCoverImageUrl(eventData?.coverImage);
      setCoverImageId(eventData?.coverImageId);
    }
    if(getValues("certificateArrangement") === "") {
      setValue("certificateArrangement", eventData?.certificateArrangement || 'NotSure');
    }
  }, [eventData]);

  useEffect(() => {
    if (coverImage) {
      setClearMedia(false);
    }
  }, [coverImage]);

  if (isEventDataPending && isEventCategoryListPending && isOrganiserListPending) {
    return (
      <div className="flex justify-center items-center h-screen">
        <p className="text-lg font-semibold">Loading...</p>
      </div>
    );
  }

  return (
    <form className="flex flex-col w-full font-semibold" onSubmit={handleSubmit(onSubmit)}>
      <div className='flex items-center justify-end gap-3'>
        <ActiveStatus active={active} setActive={setActive} isCreateForm={true} />
        <ActiveStatus active={homePage} setActive={setHomePage} isCreateForm={true} label={'Home Page'} innerActiveText='Yes' innerInactiveText='No' />
      </div>
      <section className="flex flex-col justify-center px-5 pt-2 py-9 gap-3  w-full bg-white rounded-md shadow-[0px_4px_25px_rgba(94,92,154,0.1)]">
        <div className="flex items-center justify-between">
          <p className="text-base font-bold mt-3">Event Information</p>
        </div>
        <div className="flex gap-3">
          <div className="flex flex-col w-full gap-4">
            <div className='flex items-center gap-3'>
              {/* event category */}
              <div className="flex flex-col flex-1 basis-0 min-h-[65px] w-full mt-1.5">
                <label className="gap-1 self-stretch text-base w-full text-gray-900 font-semibold capitalize">Event Category</label>
                <div className="flex gap-2.5 items-center mt-1 w-full bg-white rounded-md border border-solid border-navBtn text-slate-400">
                  <select
                    className="flex-1 select2 shrink self-stretch my-auto basis-0 px-4 py-2.5 outline-primary rounded-md capitalize text-base"
                    {...register("typeOfEvent", { required: "Type of Event is required" })}
                  >
                    <option value="">Select Event Category</option>
                    {eventCategoryList?.map((category) => (
                      <option key={category._id} value={category._id}>
                        {category.name}
                      </option>
                    ))}
                  </select>
                </div>
                {errors.typeOfEvent && <p className="text-red text-sm">{errors.typeOfEvent.message}</p>}
              </div>
              {/* Event Title */}
              <div className="flex flex-col flex-1 grow shrink-0 basis-1/3 min-h-[65px] w-full mt-1.5">
                <label className="gap-1 self-stretch text-base w-full text-gray-900 font-semibold capitalize">Event Title</label>
                <div className="flex gap-2.5 items-center mt-1 w-full bg-white rounded-md border border-solid border-navBtn text-slate-400">
                  <input
                    type="text"
                    placeholder="Event Title"
                    {...register("title", { required: "Title is required" })}
                    className={`flex-1 shrink self-stretch my-auto basis-0 outline-primary px-4 py-2.5 rounded-md text-base ${errors.title ? "border-red-500" : ""}`}
                  />
                </div>
                {errors.title && <p className="text-red text-sm">{errors.title.message}</p>}
              </div>
            </div>
            {/* Description */}
            <div className="flex flex-col min-h-[150px] w-full mt-1.5">
              <label className="gap-1 self-stretch text-base w-full text-gray-900 font-semibold capitalize">Description</label>
              <div className="flex gap-2.5 items-center mt-1 w-full bg-white rounded-md border border-solid border-navBtn text-slate-400">
                <textarea
                  type="text"
                  placeholder="Enter Description of Event"
                  {...register("description", { required: "Description is required" })}
                  className={`flex-1 shrink self-stretch my-auto basis-0 outline-primary px-4 py-2.5 rounded-md text-base ${errors.description ? "border-red-500" : ""}`}
                  rows={8}
                />
              </div>
              {errors.description && <p className="text-red text-sm">{errors.description.message}</p>}
            </div>

          </div>
          {/* Blog Cover Image */}
          <div className="mt-0">
            <MediaUpload
              clearMedia={clearMedia}
              label={"Thumbnail"}
              existingImage={coverImageUrl}
              setImageUpload={setCoverImage}
              stateImage={coverImage}
              setStateImageUpload={setCoverImage}
              useFileObject={true}
              onClear={() => {
                setCoverImageUrl(null);
                setCoverImageId(null);
              }}
            />
          </div>
        </div>

        <div className='flex gap-3'>
          {/* Start Date */}
          <div className="flex flex-col flex-1 grow shrink-0 basis-0 min-h-[65px] w-full mt-1.5">
            <label className="gap-1 self-stretch text-base w-full text-gray-900 font-semibold capitalize">Start Date</label>
            <div className="flex gap-2.5 items-center mt-1 w-full bg-white rounded-md border border-solid border-navBtn text-slate-400">
              <input
                type="date"
                placeholder="Enter Description of Event"
                {...register("startDate", { required: "Start date is required" })}
                className={`flex-1 shrink self-stretch my-auto basis-0 outline-primary px-4 py-2.5 rounded-md text-base ${errors.startDate ? "border-red-500" : ""}`}
              />
            </div>
            {errors.startDate && <p className="text-red text-sm">{errors.startDate.message}</p>}
          </div>
          {/* End Date */}
          <div className="flex flex-col flex-1 grow shrink-0 basis-0 min-h-[65px] w-full mt-1.5">
            <label className="gap-1 self-stretch text-base w-full text-gray-900 font-semibold capitalize">End Date</label>
            <div className="flex gap-2.5 items-center mt-1 w-full bg-white rounded-md border border-solid border-navBtn text-slate-400">
              <input
                type="date"
                placeholder="Enter Description of Event"
                {...register("endDate", { required: "End date is required" })}
                className={`flex-1 shrink self-stretch my-auto basis-0 outline-primary px-4 py-2.5 rounded-md text-base ${errors.endDate ? "border-red-500" : ""}`}
              />
            </div>
            {errors.endDate && <p className="text-red text-sm">{errors.endDate.message}</p>}
          </div>
          {/* Start time */}
          {/* <div className="flex flex-col flex-1 grow shrink-0 basis-0 min-h-[65px] w-full mt-1.5">
            <label className="gap-1 self-stretch text-base w-full text-gray-900 font-semibold capitalize">Start Time</label>
            <div className="flex gap-2.5 items-center mt-1 w-full bg-white rounded-md border border-solid border-navBtn text-slate-400">
              <input
                type="time"
                placeholder="Enter Description of Event"
                {...register("startTime", { required: "Start Time is required" })}
                className={`flex-1 shrink self-stretch my-auto basis-0 outline-primary px-4 py-2.5 rounded-md text-base ${errors.startTime ? "border-red-500" : ""}`}
              />
            </div>
            {errors.startTime && <p className="text-red text-sm">{errors.startTime.message}</p>}
          </div> */}
          {/* Timing Details */}
          <div className="flex flex-col flex-1 grow shrink-0 basis-0 min-h-[65px] w-full mt-1.5">
            <label className="gap-1 self-stretch text-base w-full text-gray-900 font-semibold capitalize">Timing Details</label>
            <div className="flex gap-2.5 items-center mt-1 w-full bg-white rounded-md border border-solid border-navBtn text-slate-400">
              <input
                type="text"
                placeholder="Enter Timing Details"
                {...register("timingDetails", { required: "Timing Details are required" })}
                className={`flex-1 shrink self-stretch my-auto basis-0 outline-primary px-4 py-2.5 rounded-md text-base ${errors.timingDetails ? "border-red-500" : ""}`}
              />
            </div>
            {errors.timingDetails && <p className="text-red text-sm">{errors.timingDetails.message}</p>}
          </div>
        </div>

        {/* Instructions for Volunteers */}
        <div className="flex flex-col flex-1 min-h-[65px] w-full mt-1.5">
          <label className="gap-1 self-stretch text-base w-full text-gray-900 font-semibold capitalize">Instructions for Volunteers</label>
          <div className="flex gap-2.5 items-center mt-1 w-full bg-white rounded-md border border-solid border-navBtn text-slate-400">
            <textarea
              type="text"
              placeholder="Enter Instructions for Volunteers"
              {...register("instructionsForVolunteers", { required: "Instructions for Volunteers is required" })}
              className={`flex-1 shrink self-stretch my-auto basis-0 outline-primary px-4 py-2.5 rounded-md text-base ${errors.instructionsForVolunteers ? "border-red-500" : ""}`}
              rows={5}
            />
          </div>
          {errors.instructionsForVolunteers && <p className="text-red text-sm">{errors.instructionsForVolunteers.message}</p>}
        </div>

        <div className='flex items-center gap-3'>
          {/* City */}
          <div className="flex flex-col flex-1 grow shrink-0 basis-0 min-h-[65px] w-full mt-1.5">
            <label className="gap-1 self-stretch text-base w-full text-gray-900 font-semibold capitalize">City</label>
            <div className="flex gap-2.5 items-center mt-1 w-full bg-white rounded-md border border-solid border-navBtn text-slate-400">
              <input
                type="text"
                placeholder="Enter City Name"
                {...register("city", { required: "City is required" })}
                className={`flex-1 shrink self-stretch my-auto basis-0 outline-primary px-4 py-2.5 rounded-md text-base ${errors.city ? "border-red-500" : ""}`}
              />
            </div>
            {errors.city && <p className="text-red text-sm">{errors.city.message}</p>}
          </div>
          {/* Venue Name */}
          <div className="flex flex-col flex-1 grow shrink-0 basis-0 min-h-[65px] w-full mt-1.5">
            <label className="gap-1 self-stretch text-base w-full text-gray-900 font-semibold capitalize">Venue Name</label>
            <div className="flex gap-2.5 items-center mt-1 w-full bg-white rounded-md border border-solid border-navBtn text-slate-400">
              <input
                type="text"
                placeholder="Enter Venue Name"
                {...register("location", { required: "Venue Name is required" })}
                className={`flex-1 shrink self-stretch my-auto basis-0 outline-primary px-4 py-2.5 rounded-md text-base ${errors.location ? "border-red-500" : ""}`}
              />
            </div>
            {errors.location && <p className="text-red text-sm">{errors.location.message}</p>}
          </div>
          {/* Venue Map Url */}
          <div className="flex flex-col flex-1 grow shrink-0 basis-0 min-h-[65px] w-full mt-1.5">
            <label className="gap-1 self-stretch text-base w-full text-gray-900 font-semibold capitalize">Venue Map Url</label>
            <div className="flex gap-2.5 items-center mt-1 w-full bg-white rounded-md border border-solid border-navBtn text-slate-400">
              <input
                type="text"
                placeholder="Enter Venue Map Url"
                {...register("venueMapUrl", { required: false })}
                className={`flex-1 shrink self-stretch my-auto basis-0 outline-primary px-4 py-2.5 rounded-md text-base ${errors.venueMapUrl ? "border-red-500" : ""}`}
              />
            </div>
            {errors.venueMapUrl && <p className="text-red text-sm">{errors.venueMapUrl.message}</p>}
          </div>
        </div>

        <div className='flex items-center gap-3'>
          {/* Points */}
          <div className="flex flex-col flex-1 grow shrink-0 basis-0 min-h-[65px] w-full mt-1.5">
            <label className="gap-1 self-stretch text-base w-full text-gray-900 font-semibold capitalize">Points</label>
            <div className="flex gap-2.5 items-center mt-1 w-full bg-white rounded-md border border-solid border-navBtn text-slate-400">
              <input
                type="number"
                placeholder="Enter Points"
                {...register("points", {
                  required: "Points is required",
                  min: { value: 0, message: "Points must be at least 0" },
                  validate: value => /^\d+$/.test(value) || "Only numbers are allowed"
                })}
                className={`flex-1 shrink self-stretch my-auto basis-0 outline-primary px-4 py-2.5 rounded-md text-base ${errors.points ? "border-red-500" : ""}`}
                inputMode="numeric"
                pattern="[0-9]*"
              />
            </div>
            {errors.points && <p className="text-red text-sm">{errors.points.message}</p>}
          </div>
          {/* Event Organiser */}
          <div className="flex flex-col flex-1 grow shrink-0 basis-0 min-h-[65px] w-full mt-1.5">
            <label className="gap-1 self-stretch text-base w-full text-gray-900 font-semibold capitalize">Event Organiser</label>
            <div className="flex gap-2.5 items-center mt-1 w-full bg-white rounded-md border border-solid border-navBtn text-slate-400">
              {/* <input
                type="text"
                placeholder="Enter Event Organiser"
                {...register("eventOrganiser", { required: "Event Organiser is required" })}
                className={`flex-1 shrink self-stretch my-auto basis-0 outline-primary px-4 py-2.5 rounded-md text-base ${errors.eventOrganiser ? "border-red-500" : ""}`}
              /> */}
              <Dropdown
                register={register}
                name="eventOrganiser"
                requiredMessage={false}
                dataList={organiserList}
                placeholder="Select Event Organiser"
              />
            </div>
            {errors.eventOrganiser && <p className="text-red text-sm">{errors.eventOrganiser.message}</p>}
          </div>
          {/* Certificate Arrangement */}
          <div className="flex flex-col flex-1 grow shrink-0 basis-0 min-h-[65px] w-full mt-1.5">
            <label className="gap-1 self-stretch text-base w-full text-gray-900 font-semibold capitalize">Certificate Arrangement</label>
            <div className="flex gap-2.5 items-center mt-1 w-full bg-white rounded-md border border-solid border-navBtn text-slate-400">
              <Dropdown
                register={register}
                name="certificateArrangement"
                requiredMessage={"Certificate Arrangement is required"}
                dataList={[
                  { name: 'No Certificate', _id: "No" },
                  { name: 'During Event Directly to volunteer', _id: "During" },
                  { name: 'After Event to Climate Crusaders', _id: "After" },
                  { name: 'Not Sure', _id: "NotSure" },
                ]}
                placeholder="Select Certificate Arrangement"
              />
            </div>
            {errors.certificateArrangement && <p className="text-red text-sm">{errors.certificateArrangement.message}</p>}
          </div>
        </div>
        {/* <div className="flex flex-col flex-1 grow shrink-0 basis-0 min-h-[65px] w-full mt-1.5">
          <label className="gap-1 self-stretch text-base w-full text-gray-900 font-semibold capitalize">Event Sponsor</label>
          <div className="flex gap-2.5 items-center mt-1 w-full bg-white rounded-md border border-solid border-navBtn text-slate-400">
            <input
              type="text"
              placeholder="Enter Event Sponsor NGO/Company"
              {...register("eventSponsor")}
              className={`flex-1 shrink self-stretch my-auto basis-0 outline-primary px-4 py-2.5 rounded-md text-base ${errors.eventSponsor ? "border-red-500" : ""}`}
            />
          </div>
          {errors.eventSponsor && <p className="text-red text-sm">{errors.eventSponsor.message}</p>}
        </div> */}
        <div className="mt-10 flex gap-5 items-center justify-between">
          <ActionButton
            variant="reset"
            text="Reset"
            handleSubmit={() => {
              reset();
              setActive(true);
              setHomePage(false);
              setCoverImage(null);
              setClearMedia(true);
              setValue("title", "");
              setValue("description", "");
              setValue("startDate", "");
              setValue("endDate", "");
              setValue("timingDetails", "");
              setValue("instructionsForVolunteers", "");
              setValue("location", "");
              setValue("eventOrganiser", "");
              setValue("typeOfEvent", "");
              setValue("points", "");
              setValue("eventSponsor", "");
            }}
          />
          <div className="flex items-center gap-3">
            <ActionButton
              variant="cancel"
              text="Cancel"
              handleSubmit={() => navigate('/events/events-main/view-events')}
            />
            <ActionButton
              variant="submit"
              text="Save"
            // disabled={isCreatingBlog || isEditingBlog}
            />
          </div>
        </div>
      </section>
      <ToastContainer
        position="top-right"
      />
    </form>
  )
}

export default CreateEvent